/**
 * hooks/useFeeHierarchy.ts
 * Simplified fee hierarchy management hook for onboarding fee configuration
 *
 * Uses native tree structure that mirrors the UI directly for better maintainability
 * and performance. No more complex transformations - direct tree operations.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { BrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';
import type { AssetClass, SubAssetClass } from '@/types/shared/AssetClass';
import { useOnboardingData } from '../../../hooks/useOnboardingData';

/**
 * UI-friendly fee representation for display
 */
export interface DisplayFeeConfig {
  commission?: string | null;
  overnight?: string | null;
  conversion?: string | null;
  notes?: string | null;
}

/**
 * Tree node representing a level in the fee hierarchy
 * Structure mirrors the UI accordion structure directly
 */
export interface TreeNode {
  /** Unique identifier for this node */
  id: string;
  /** Display name for this level */
  name: string;
  /** Type of hierarchy level */
  type: 'asset-class' | 'market-segment' | 'sub-asset-class';
  /** Parent node ID (undefined for root nodes) */
  parentId?: string;
  /** Fee configuration for this level */
  fees?: DisplayFeeConfig | null;
  /** Original BrokerFeeConfiguration ID for persistence */
  configId?: string;
  /** Child hierarchy levels */
  children: TreeNode[];
  /** Individual instruments at this level */
  instruments: InstrumentNode[];
  /** Raw values for building taxonomy */
  rawValues: {
    assetClass: string;
    marketSegment?: string;
    subAssetClass?: string;
  };
}

/**
 * Instrument node with fee configuration
 */
export interface InstrumentNode {
  /** Unique identifier */
  id: string;
  /** Instrument symbol */
  symbol: string;
  /** Display name */
  displayName: string;
  /** Fee configuration for this instrument */
  fees?: DisplayFeeConfig | null;
  /** Original BrokerFeeConfiguration ID for persistence */
  configId?: string;
  /** Taxonomy information */
  taxonomy: {
    assetClass: string;
    marketSegment?: string;
    subAssetClass?: string;
  };
}

/**
 * Complete hierarchy structure - array of root asset class nodes
 */
export type FeeHierarchy = TreeNode[];

/**
 * Operations supported by the hierarchy manager
 */
export interface FeeHierarchyOperations {
  /** Add or update fee configuration at hierarchy level */
  assignFees: (nodeId: string, feeConfig: BrokerFeeConfiguration) => void;
  /** Remove fees from hierarchy level */
  removeFees: (nodeId: string) => void;
  /** Remove entire hierarchy level and its children */
  removeLevel: (nodeId: string) => void;
  /** Add new asset class level */
  addAssetClass: (assetClass: string, customName?: string) => void;
  /** Add new market segment under asset class */
  addMarketSegment: (parentNodeId: string, marketSegment: string, customName?: string) => void;
  /** Add new sub-asset class under market segment */
  addSubAssetClass: (parentNodeId: string, subAssetClass: string, customName?: string) => void;
  /** Add instrument to specific node */
  addInstrument: (nodeId: string, instrument: string, feeConfig: BrokerFeeConfiguration) => void;
  /** Remove instrument from hierarchy */
  removeInstrument: (instrumentId: string) => void;
  /** Assign or update fees for a specific instrument */
  assignInstrumentFees: (instrumentId: string, feeConfig: BrokerFeeConfiguration) => void;
  /** Build hierarchy from selected instruments (auto-generate taxonomy) */
  buildFromInstruments: (instruments: Array<{ symbol: string; assetClass: string; marketSegment?: string; subAssetClass?: string; catalogId: string; }>) => void;
  /** Find node by ID */
  findNode: (nodeId: string) => TreeNode | null;
  /** Find instrument by ID */
  findInstrument: (instrumentId: string) => { node: TreeNode; instrument: InstrumentNode } | null;
}

/**
 * Utility Functions
 */

/**
 * Generate unique ID for nodes and instruments
 */
const generateId = (): string => {
  return `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Convert BrokerFeeConfiguration to DisplayFeeConfig for UI
 */
const toDisplayFeeConfig = (config: BrokerFeeConfiguration): DisplayFeeConfig => {
  const commission = config.commission ? formatCommissionDisplay(config.commission) : null;
  const overnight = config.overnightFinancing ? formatOvernightDisplay(config.overnightFinancing) : null;
  const conversion = config.currencyConversion ? formatConversionDisplay(config.currencyConversion) : null;
  const notes = config.notes || null;

  return { commission, overnight, conversion, notes };
};

/**
 * Format commission structure for display
 */
const formatCommissionDisplay = (commission: BrokerFeeConfiguration['commission']): string => {
  if (!commission || !commission.feeAmount) return '';

  const feeAmount = commission.feeAmount;
  const precision = commission.feePrecision || 2;
  const value = feeAmount / Math.pow(10, precision);

  switch (commission.feeType) {
    case 'fixedPerUnit': {
      const chargeTiming = commission.chargeOn === 'roundTrip' ? '/round trip' : '/side';
      return `$${value.toFixed(2)}/contract${chargeTiming}`;
    }
    case 'perShare':
      return `$${value.toFixed(precision)}/share`;
    case 'percentage':
      return `${value.toFixed(2)}%`;
    case 'fixedPerTransaction': {
      const fixedTiming = commission.chargeOn === 'roundTrip' ? '/round trip' : '/side';
      return `$${value.toFixed(2)}${fixedTiming}`;
    }
    case 'tiered':
      return `Tiered: starting at $${value.toFixed(2)}`;
    case 'makerTaker':
      return `Maker/Taker fees`;
    case 'spreadOnly':
      return 'Spread only';
    default:
      return `$${value.toFixed(2)}`;
  }
};

/**
 * Format overnight financing for display
 */
const formatOvernightDisplay = (overnight: NonNullable<BrokerFeeConfiguration['overnightFinancing']>): string => {
  if (!overnight.benchmark) return 'Custom overnight rate';

  const spread = overnight.adminSpread / Math.pow(10, overnight.adminSpreadPrecision || 2);
  const spreadStr = spread >= 0 ? `+ ${spread}%` : `- ${Math.abs(spread)}%`;

  return `${overnight.benchmark} ${spreadStr}`;
};

/**
 * Format currency conversion for display
 */
const formatConversionDisplay = (conversion: NonNullable<BrokerFeeConfiguration['currencyConversion']>): string => {
  if (!conversion.markup) return 'Custom conversion rate';

  const markup = conversion.markup / Math.pow(10, conversion.markupPrecision || 2);
  return `${markup}% markup`;
};

/**
 * Tree utility functions
 */

/**
 * Find a node by ID in the tree (recursive search)
 */
const findNodeInTree = (nodes: TreeNode[], nodeId: string): TreeNode | null => {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }
    const found = findNodeInTree(node.children, nodeId);
    if (found) {
      return found;
    }
  }
  return null;
};

/**
 * Find an instrument by ID in the tree (recursive search)
 */
const findInstrumentInTree = (nodes: TreeNode[], instrumentId: string): { node: TreeNode; instrument: InstrumentNode } | null => {
  for (const node of nodes) {
    const instrument = node.instruments.find(inst => inst.id === instrumentId);
    if (instrument) {
      return { node, instrument };
    }
    const found = findInstrumentInTree(node.children, instrumentId);
    if (found) {
      return found;
    }
  }
  return null;
};

/**
 * Update a node in the tree (immutable)
 */
const updateNodeInTree = (nodes: TreeNode[], nodeId: string, updates: Partial<TreeNode>): TreeNode[] => {
  return nodes.map(node => {
    if (node.id === nodeId) {
      return { ...node, ...updates };
    }
    if (node.children.length > 0) {
      return {
        ...node,
        children: updateNodeInTree(node.children, nodeId, updates)
      };
    }
    return node;
  });
};

/**
 * Remove a node from the tree (immutable)
 */
const removeNodeFromTree = (nodes: TreeNode[], nodeId: string): TreeNode[] => {
  return nodes
    .filter(node => node.id !== nodeId)
    .map(node => ({
      ...node,
      children: removeNodeFromTree(node.children, nodeId)
    }));
};

/**
 * Add a child node to a parent (immutable)
 */
const addChildToNode = (nodes: TreeNode[], parentId: string, childNode: TreeNode): TreeNode[] => {
  return nodes.map(node => {
    if (node.id === parentId) {
      return {
        ...node,
        children: [...node.children, childNode]
      };
    }
    if (node.children.length > 0) {
      return {
        ...node,
        children: addChildToNode(node.children, parentId, childNode)
      };
    }
    return node;
  });
};

/**
 * Convert tree structure to flat BrokerFeeConfiguration array for persistence
 */
const treeToFlatConfigs = (nodes: TreeNode[]): BrokerFeeConfiguration[] => {
  const configs: BrokerFeeConfiguration[] = [];

  const processNode = (node: TreeNode) => {
    // ALWAYS persist hierarchy structure, even without fees
    // This creates placeholder configurations to maintain the hierarchy
    const config: BrokerFeeConfiguration = {
      id: node.configId || generateId(),
      name: `${node.name} fees`,
      category: {
        assetClass: node.rawValues.assetClass as AssetClass['assetClass'],
        marketSegment: (node.rawValues.marketSegment || '') as AssetClass['marketSegment']
      },
      commission: {
        feeType: 'fixedPerUnit',
        feeAmount: 0, // Default to 0 for placeholder
        feePrecision: 2,
        chargeOn: 'perSide',
        verificationStatus: 'userOverride'
      }
    };

    if (node.rawValues.subAssetClass && config.category) {
      config.category.subAssetClass = node.rawValues.subAssetClass as SubAssetClass;
    }

    // Override with actual fee data if fees exist
    if (node.fees && (node.fees.commission || node.fees.overnight || node.fees.conversion)) {
      // Parse commission data from display format back to config format
      if (node.fees.commission) {
        // Extract amount (handles $5, $5.50, 5.50/contract, etc.)
        const amountMatch = node.fees.commission.match(/(\d+(?:\.\d+)?)/);
        if (amountMatch && amountMatch.length > 1 && amountMatch[1]) {
          const rawAmount = parseFloat(amountMatch[1]);
          const precision = config.commission.feePrecision || 2;
          // Convert to integer storage format (e.g., $5.50 -> 550 for precision=2)
          config.commission.feeAmount = Math.round(rawAmount * Math.pow(10, precision));
        }

        // Determine fee type and charge basis from display format
        if (node.fees.commission.toLowerCase().includes('contract')) {
          config.commission.feeType = 'fixedPerUnit';
        } else if (node.fees.commission.toLowerCase().includes('%')) {
          config.commission.feeType = 'percentage';
        } else if (node.fees.commission.toLowerCase().includes('/side') || node.fees.commission.toLowerCase().includes('/round trip')) {
          config.commission.feeType = 'fixedPerTransaction';
        } else {
          config.commission.feeType = 'fixedPerTransaction'; // Default for fixed amounts
        }

        // Determine charge timing
        if (node.fees.commission.toLowerCase().includes('round trip')) {
          config.commission.chargeOn = 'roundTrip';
        } else {
          config.commission.chargeOn = 'perSide';
        }
      }

      // Add overnight financing if present
      if (node.fees.overnight) {
        // Parse overnight display format like "SOFR + 2.5%" or "LIBOR - 1.0%"
        const overnightMatch = node.fees.overnight.match(/([A-Z]+)?\s*([+-]?)\s*(\d+(?:\.\d+)?)/);
        const benchmark = overnightMatch?.[1] || 'SOFR';
        const sign = overnightMatch?.[2] === '-' ? -1 : 1;
        const spreadValue = overnightMatch?.[3] ? parseFloat(overnightMatch[3]) * sign : 0.5;

        config.overnightFinancing = {
          benchmark,
          adminSpread: Math.round(spreadValue * 100), // Convert to basis points (2.5% -> 250)
          adminSpreadPrecision: 2,
          frequency: 'daily',
          verificationStatus: 'userOverride'
        };
      }

      // Add currency conversion if present
      if (node.fees.conversion) {
        config.currencyConversion = {
          markup: 0.25, // Default - would need to parse from display format
          markupPrecision: 2
        };
      }

      // Add notes if present
      if (node.fees.notes) {
        config.notes = node.fees.notes;
      }
    }

    configs.push(config);

    // Add instrument configurations (always persist, even without fees)
    node.instruments.forEach(instrument => {
      const config: BrokerFeeConfiguration = {
        id: instrument.configId || generateId(),
        name: `${instrument.symbol} fees`,
        instrumentId: instrument.symbol,
        category: {
          assetClass: instrument.taxonomy.assetClass as AssetClass['assetClass'],
          marketSegment: (instrument.taxonomy.marketSegment || '') as AssetClass['marketSegment']
        },
        commission: {
          feeType: 'fixedPerUnit',
          feeAmount: 0, // Default to 0 for placeholder
          feePrecision: 2,
          chargeOn: 'perSide',
          verificationStatus: 'userOverride'
        }
      };

      if (instrument.taxonomy.subAssetClass && config.category) {
        config.category.subAssetClass = instrument.taxonomy.subAssetClass as SubAssetClass;
      }

      // Override with actual fee data if fees exist
      if (instrument.fees && (instrument.fees.commission || instrument.fees.overnight || instrument.fees.conversion)) {
        // Parse commission data from display format back to config format
        if (instrument.fees.commission) {
          // Extract amount (handles $5, $5.50, 5.50/contract, etc.)
          const amountMatch = instrument.fees.commission.match(/(\d+(?:\.\d+)?)/);
          if (amountMatch && amountMatch.length > 1 && amountMatch[1]) {
            const rawAmount = parseFloat(amountMatch[1]);
            const precision = config.commission.feePrecision || 2;
            // Convert to integer storage format (e.g., $5.50 -> 550 for precision=2)
            config.commission.feeAmount = Math.round(rawAmount * Math.pow(10, precision));
          }

          // Determine fee type and charge basis from display format
          if (instrument.fees.commission.toLowerCase().includes('contract')) {
            config.commission.feeType = 'fixedPerUnit';
          } else if (instrument.fees.commission.toLowerCase().includes('%')) {
            config.commission.feeType = 'percentage';
          } else if (instrument.fees.commission.toLowerCase().includes('/side') || instrument.fees.commission.toLowerCase().includes('/round trip')) {
            config.commission.feeType = 'fixedPerTransaction';
          } else {
            config.commission.feeType = 'fixedPerTransaction'; // Default for fixed amounts
          }

          // Determine charge timing
          if (instrument.fees.commission.toLowerCase().includes('round trip')) {
            config.commission.chargeOn = 'roundTrip';
          } else {
            config.commission.chargeOn = 'perSide';
          }
        }

        // Add overnight financing if present
        if (instrument.fees.overnight) {
          // Parse overnight display format like "SOFR + 2.5%" or "LIBOR - 1.0%"
          const overnightMatch = instrument.fees.overnight.match(/([A-Z]+)?\s*([+-]?)\s*(\d+(?:\.\d+)?)/);
          const benchmark = overnightMatch?.[1] || 'SOFR';
          const sign = overnightMatch?.[2] === '-' ? -1 : 1;
          const spreadValue = overnightMatch?.[3] ? parseFloat(overnightMatch[3]) * sign : 0.5;

          config.overnightFinancing = {
            benchmark,
            adminSpread: Math.round(spreadValue * 100), // Convert to basis points (2.5% -> 250)
            adminSpreadPrecision: 2,
            frequency: 'daily',
            verificationStatus: 'userOverride'
          };
        }

        // Add currency conversion if present
        if (instrument.fees.conversion) {
          config.currencyConversion = {
            markup: 0.25, // Default - would need to parse from display format
            markupPrecision: 2
          };
        }

        // Add notes if present
        if (instrument.fees.notes) {
          config.notes = instrument.fees.notes;
        }
      }

      configs.push(config);
    });

    // Process children recursively
    node.children.forEach(processNode);
  };

  nodes.forEach(processNode);
  return configs;
};

/**
 * Build tree structure from flat BrokerFeeConfiguration array
 */
const flatConfigsToTree = (configs: BrokerFeeConfiguration[]): TreeNode[] => {
  const rootNodes: TreeNode[] = [];
  const nodeMap = new Map<string, TreeNode>();

  // First pass: create all nodes
  configs.forEach(config => {
    if (!config.category) return;

    const assetClass = config.category.assetClass;
    const marketSegment = config.category.marketSegment;
    const subAssetClass = config.category.subAssetClass;

    // Create asset class node if it doesn't exist
    const assetClassKey = assetClass;
    if (!nodeMap.has(assetClassKey)) {
      const assetClassNode: TreeNode = {
        id: generateId(),
        name: assetClass,
        type: 'asset-class',
        children: [],
        instruments: [],
        rawValues: { assetClass }
      };
      nodeMap.set(assetClassKey, assetClassNode);
      rootNodes.push(assetClassNode);
    }

    // Create market segment node if specified
    if (marketSegment && marketSegment.trim()) {
      const marketSegmentKey = `${assetClass}→${marketSegment}`;
      if (!nodeMap.has(marketSegmentKey)) {
        const assetClassNode = nodeMap.get(assetClassKey);
        if (!assetClassNode) {
          console.warn(`Asset class node not found for key: ${assetClassKey}`);
          return;
        }

        const marketSegmentNode: TreeNode = {
          id: generateId(),
          name: marketSegment,
          type: 'market-segment',
          parentId: assetClassNode.id,
          children: [],
          instruments: [],
          rawValues: { assetClass, marketSegment }
        };
        nodeMap.set(marketSegmentKey, marketSegmentNode);
        assetClassNode.children.push(marketSegmentNode);
      }
    }

    // Create sub-asset class node if specified
    if (subAssetClass && marketSegment && marketSegment.trim()) {
      const subAssetClassKey = `${assetClass}→${marketSegment}→${subAssetClass}`;
      if (!nodeMap.has(subAssetClassKey)) {
        const marketSegmentNode = nodeMap.get(`${assetClass}→${marketSegment}`);
        if (!marketSegmentNode) {
          console.warn(`Market segment node not found for key: ${assetClass}→${marketSegment}`);
          return;
        }

        const subAssetClassNode: TreeNode = {
          id: generateId(),
          name: subAssetClass,
          type: 'sub-asset-class',
          parentId: marketSegmentNode.id,
          children: [],
          instruments: [],
          rawValues: { assetClass, marketSegment, subAssetClass }
        };
        nodeMap.set(subAssetClassKey, subAssetClassNode);
        marketSegmentNode.children.push(subAssetClassNode);
      }
    }
  });

  // Second pass: assign fees and instruments
  configs.forEach(config => {
    if (!config.category) return;

    const assetClass = config.category.assetClass;
    const marketSegment = config.category.marketSegment;
    const subAssetClass = config.category.subAssetClass;

    if (config.instrumentId) {
      // This is an instrument configuration
      let targetNode: TreeNode;

      if (subAssetClass && marketSegment && marketSegment.trim()) {
        const foundNode = nodeMap.get(`${assetClass}→${marketSegment}→${subAssetClass}`);
        if (!foundNode) return;
        targetNode = foundNode;
      } else if (marketSegment && marketSegment.trim()) {
        const foundNode = nodeMap.get(`${assetClass}→${marketSegment}`);
        if (!foundNode) return;
        targetNode = foundNode;
      } else {
        const foundNode = nodeMap.get(assetClass);
        if (!foundNode) return;
        targetNode = foundNode;
      }

      const instrument: InstrumentNode = {
        id: config.instrumentId,
        symbol: config.instrumentId,
        displayName: config.name,
        fees: toDisplayFeeConfig(config),
        configId: config.id,
        taxonomy: {
          assetClass,
          ...(marketSegment && { marketSegment }),
          ...(subAssetClass && { subAssetClass })
        }
      };

      targetNode.instruments.push(instrument);
    } else {
      // This is a hierarchy-level configuration
      let targetKey: string;

      if (subAssetClass && marketSegment && marketSegment.trim()) {
        targetKey = `${assetClass}→${marketSegment}→${subAssetClass}`;
      } else if (marketSegment && marketSegment.trim()) {
        targetKey = `${assetClass}→${marketSegment}`;
      } else {
        targetKey = assetClass;
      }

      const targetNode = nodeMap.get(targetKey);
      if (targetNode) {
        targetNode.fees = toDisplayFeeConfig(config);
        targetNode.configId = config.id;
      }
    }
  });

  return rootNodes;
};

/**
 * Simplified fee hierarchy management hook
 */
export const useFeeHierarchy = (): [FeeHierarchy, FeeHierarchyOperations] => {
  const { draft, updateFeeConfiguration } = useOnboardingData();

  // Initialize tree synchronously from draft to prevent flash
  const [tree, setTree] = useState<FeeHierarchy>(() => {
    const feeConfigurations = draft?.account?.feeConfiguration?.customFeeConfigs || [];
    if (feeConfigurations.length > 0) {
      console.log('🏗️ INITIAL TREE BUILD FROM CONFIGS:', feeConfigurations.length, 'configurations');
      return flatConfigsToTree(feeConfigurations);
    }
    return [];
  });

  // Initialize lastConfigsRef with initial hash
  const initialHash = (() => {
    const feeConfigurations = draft?.account?.feeConfiguration?.customFeeConfigs || [];
    return JSON.stringify(feeConfigurations.map(c => c.id).sort());
  })();
  const lastConfigsRef = useRef<string>(initialHash);

  // Get current fee configurations from draft and build tree
  // Use ref to prevent rebuilding when array reference changes but content is same
  useEffect(() => {
    const feeConfigurations = draft?.account?.feeConfiguration?.customFeeConfigs || [];

    // Create stable hash of configs to detect actual changes
    const configsHash = JSON.stringify(feeConfigurations.map(c => c.id).sort());

    // Only rebuild if configs actually changed
    if (configsHash !== lastConfigsRef.current) {
      console.log('🏗️ BUILDING TREE FROM CONFIGS:', feeConfigurations.length, 'configurations');
      lastConfigsRef.current = configsHash;

      const newTree = flatConfigsToTree(feeConfigurations);
      setTree(newTree);
    }
  }, [draft?.account?.feeConfiguration?.customFeeConfigs]);

  // Persist tree changes back to onboarding draft
  const persistTree = useCallback((newTree: FeeHierarchy) => {
    const flatConfigs = treeToFlatConfigs(newTree);
    console.log('💾 PERSISTING TREE:', flatConfigs.length, 'configurations');
    updateFeeConfiguration(flatConfigs);
  }, [updateFeeConfiguration]);
  
  // Simplified tree operations
  const operations: FeeHierarchyOperations = {
    assignFees: useCallback((nodeId: string, feeConfig: BrokerFeeConfiguration) => {
      const newTree = updateNodeInTree(tree, nodeId, {
        fees: toDisplayFeeConfig(feeConfig),
        configId: feeConfig.id
      });
      setTree(newTree);
      persistTree(newTree);
    }, [tree, persistTree]),
    
    removeFees: useCallback((nodeId: string) => {
      const newTree = updateNodeInTree(tree, nodeId, {
        fees: null
        // configId intentionally omitted - it will be preserved or removed based on the existing node structure
      });
      setTree(newTree);
      persistTree(newTree);
    }, [tree, persistTree]),
    
    removeLevel: useCallback((nodeId: string) => {
      const newTree = removeNodeFromTree(tree, nodeId);
      setTree(newTree);
      persistTree(newTree);
    }, [tree, persistTree]),
    
    addAssetClass: useCallback((assetClass: string, customName?: string) => {
      const newNode: TreeNode = {
        id: generateId(),
        name: customName || assetClass,
        type: 'asset-class',
        children: [],
        instruments: [],
        rawValues: { assetClass }
      };

      const newTree = [...tree, newNode];
      setTree(newTree);
      persistTree(newTree);
      console.log(`Asset class ${customName || assetClass} added to hierarchy`);
    }, [tree, persistTree]),
    
    addMarketSegment: useCallback((parentNodeId: string, marketSegment: string, customName?: string) => {
      const parentNode = findNodeInTree(tree, parentNodeId);
      if (!parentNode) return;

      const newNode: TreeNode = {
        id: generateId(),
        name: customName || marketSegment,
        type: 'market-segment',
        parentId: parentNodeId,
        children: [],
        instruments: [],
        rawValues: {
          assetClass: parentNode.rawValues.assetClass,
          marketSegment: marketSegment
        }
      };

      const newTree = addChildToNode(tree, parentNodeId, newNode);
      setTree(newTree);
      persistTree(newTree);
      console.log(`Market segment ${customName || marketSegment} added to hierarchy`);
    }, [tree, persistTree]),

    addSubAssetClass: useCallback((parentNodeId: string, subAssetClass: string, customName?: string) => {
      const parentNode = findNodeInTree(tree, parentNodeId);
      if (!parentNode) return;

      const newNode: TreeNode = {
        id: generateId(),
        name: customName || subAssetClass,
        type: 'sub-asset-class',
        parentId: parentNodeId,
        children: [],
        instruments: [],
        rawValues: {
          ...parentNode.rawValues,
          subAssetClass: subAssetClass
        }
      };

      const newTree = addChildToNode(tree, parentNodeId, newNode);
      setTree(newTree);
      persistTree(newTree);
      console.log(`Sub-asset class ${customName || subAssetClass} added to hierarchy`);
    }, [tree, persistTree]),

    addInstrument: useCallback((nodeId: string, instrument: string, feeConfig: BrokerFeeConfiguration) => {
      const targetNode = findNodeInTree(tree, nodeId);
      if (!targetNode) return;

      const newInstrument: InstrumentNode = {
        id: instrument,
        symbol: instrument,
        displayName: `${instrument} fees`,
        fees: toDisplayFeeConfig(feeConfig),
        configId: feeConfig.id,
        taxonomy: targetNode.rawValues
      };

      const newTree = updateNodeInTree(tree, nodeId, {
        instruments: [...targetNode.instruments, newInstrument]
      });
      setTree(newTree);
      persistTree(newTree);
    }, [tree, persistTree]),

    removeInstrument: useCallback((instrumentId: string) => {
      const instrumentInfo = findInstrumentInTree(tree, instrumentId);
      if (!instrumentInfo) return;

      const { node } = instrumentInfo;
      const updatedInstruments = node.instruments.filter(inst => inst.id !== instrumentId);

      const newTree = updateNodeInTree(tree, node.id, {
        instruments: updatedInstruments
      });
      setTree(newTree);
      persistTree(newTree);
    }, [tree, persistTree]),

    assignInstrumentFees: useCallback((instrumentId: string, feeConfig: BrokerFeeConfiguration) => {
      const instrumentInfo = findInstrumentInTree(tree, instrumentId);
      if (!instrumentInfo) return;

      const { node, instrument } = instrumentInfo;
      const updatedInstrument = {
        ...instrument,
        fees: toDisplayFeeConfig(feeConfig),
        configId: feeConfig.id
      };

      const updatedInstruments = node.instruments.map(inst =>
        inst.id === instrumentId ? updatedInstrument : inst
      );

      const newTree = updateNodeInTree(tree, node.id, {
        instruments: updatedInstruments
      });
      setTree(newTree);
      persistTree(newTree);
    }, [tree, persistTree]),

    buildFromInstruments: useCallback((instruments: Array<{ symbol: string; assetClass: string; marketSegment?: string; subAssetClass?: string; catalogId: string; }>) => {
      console.log('🚀 STEP 4: Building hierarchy from instruments', instruments);

      // Build tree structure from instruments - much simpler than the old approach
      const updatedTree = [...tree];

      instruments.forEach(instrument => {
        // Find or create the appropriate nodes in the tree
        let currentLevel = updatedTree;
        let currentNode: TreeNode | undefined;

        // Asset class level
        currentNode = currentLevel.find(node => node.rawValues.assetClass === instrument.assetClass);
        if (!currentNode) {
          currentNode = {
            id: generateId(),
            name: instrument.assetClass,
            type: 'asset-class',
            children: [],
            instruments: [],
            rawValues: { assetClass: instrument.assetClass }
          };
          currentLevel.push(currentNode);
        }

        // Market segment level
        if (instrument.marketSegment) {
          currentLevel = currentNode.children;
          let marketSegmentNode = currentLevel.find(node =>
            node.rawValues.marketSegment === instrument.marketSegment
          );
          if (!marketSegmentNode) {
            marketSegmentNode = {
              id: generateId(),
              name: instrument.marketSegment,
              type: 'market-segment',
              parentId: currentNode.id,
              children: [],
              instruments: [],
              rawValues: {
                assetClass: instrument.assetClass,
                marketSegment: instrument.marketSegment
              }
            };
            currentLevel.push(marketSegmentNode);
          }
          currentNode = marketSegmentNode;
        }

        // Sub-asset class level
        if (instrument.subAssetClass && instrument.marketSegment) {
          currentLevel = currentNode.children;
          let subAssetClassNode = currentLevel.find(node =>
            node.rawValues.subAssetClass === instrument.subAssetClass
          );
          if (!subAssetClassNode) {
            subAssetClassNode = {
              id: generateId(),
              name: instrument.subAssetClass,
              type: 'sub-asset-class',
              parentId: currentNode.id,
              children: [],
              instruments: [],
              rawValues: {
                assetClass: instrument.assetClass,
                marketSegment: instrument.marketSegment,
                subAssetClass: instrument.subAssetClass
              }
            };
            currentLevel.push(subAssetClassNode);
          }
          currentNode = subAssetClassNode;
        }

        // Add instrument to the appropriate level
        const newInstrument: InstrumentNode = {
          id: instrument.symbol,
          symbol: instrument.symbol,
          displayName: instrument.symbol,
          taxonomy: {
            assetClass: instrument.assetClass,
            ...(instrument.marketSegment && { marketSegment: instrument.marketSegment }),
            ...(instrument.subAssetClass && { subAssetClass: instrument.subAssetClass })
          }
        };

        currentNode.instruments.push(newInstrument);
      });

      setTree(updatedTree);
      persistTree(updatedTree);
      console.log('🚀 STEP 4: Built hierarchy from instruments - created tree structure');
    }, [tree, persistTree]),

    findNode: useCallback((nodeId: string) => {
      return findNodeInTree(tree, nodeId);
    }, [tree]),

    findInstrument: useCallback((instrumentId: string) => {
      return findInstrumentInTree(tree, instrumentId);
    }, [tree])
  };

  return [tree, operations];
};